import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { googleLogout } from '@react-oauth/google';
import LoadingScreen from './components/LoadingScreen';
import Header from './components/Header';
import Review from './components/Review';
import ReviewsList from './components/ReviewsList';
import ReviewForm from './pages/ReviewForm';
import LoginForm from './components/LoginForm';
import Sidebar from './components/Sidebar';
import HomePage from './pages/Home';
import Inbox from './pages/Inbox';
import Settings from './pages/Settings';
import UserInfo from './pages/UserInfo';
import Dashboard from './pages/Dashboard';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ReviewProvider } from './context/ReviewContext';

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: Arial, sans-serif;
    background-color:rgb(255, 255, 255);
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
  }
`;

const Content = styled.div`
  padding-left: 200px;
  width: 100%;
  min-height: 100vh;  
`;

const App = () => {
  const [user, setUser] = useState(null); //user: Currently logged-in user info.
  const [reviewsData, setReviewsData] = useState([]); //reviewsData: Array of review objects.
  const [currentPage, setCurrentPage] = useState(1); //currentPage: Current page number for reviews.
  const [totalReviews, setTotalReviews] = useState(0); //totalReviews: Total number of reviews (for pagination).
  const [isLoading, setIsLoading] = useState(true); //isLoading: If app is checking auth/loading.
  // const [isSidebarOpen, setIsSidebarOpen] = useState(false); //isSidebarOpen: Sidebar visibility state.
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/current_user', {
        credentials: 'include',
      });

      if (response.ok) {
        const user = await response.json();
        if (!user.error) {
          setUser(user);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch full user details including QR, designation, etc. //
  useEffect(() => {
    if (user && user.email) { // This checks if the user state exists and has an email property
      fetch('/api/current_user', {
        credentials: 'include', // send cookies
      })
        .then((res) => res.json())
        .then((data) => { //After JSON parsing, this callback receives the actual data object.
          if (!data.error) {
            setUser(data); // update user with full detailed info
          }
        })
        .catch((err) => console.error('Error fetching current user:', err));
    }
  }, [user?.email]); //The array [user?.email] is the dependency array It means this effect will re-run only when the user.email value changes.The optional chaining user?.email makes sure it won’t break if user is null or undefined.

  useEffect(() => {
    if (user) {
      fetch(`/api/reviews?page=${currentPage}`)
        .then((response) => response.json())
        .then((data) => {
          setReviewsData(data.reviews);
          setTotalReviews(data.total_reviews);
        })
        .catch((error) => console.error('Error fetching reviews:', error));
    }
  }, [currentPage, user]);

  const handleReportInappropriate = (reportedReview) => {
    setReviewsData((prevReviews) => {
      const updatedReviews = prevReviews.filter(
        (review) => review._id.$oid !== reportedReview._id.$oid
      );
      return [...updatedReviews, reportedReview];
    });
  };

  const handleNextPage = () => {
    if (currentPage * 10 < totalReviews) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleLogout = async () => { //Logs out the user both client and server side.
    try {
      googleLogout();
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include',
      });
      setUser(null);
    } catch (error) {
      console.error("Logout failed:", error);
      setUser(null);
    }
  };

  const handleToggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return <div>Loading...</div>;
  }

  const LocationWrapper = () => {
    const location = useLocation();

    return (
      <>
        {user && user.role !== 'Viewer' && (
          <>
            <Header onLogout={handleLogout} onToggleSidebar={handleToggleSidebar} />
            {isSidebarOpen && <Sidebar />}
          </>
        )}
      </>
    );
  };

  return (
    <ReviewProvider>
      <Router>
        <GlobalStyle />
        <LocationWrapper />

        <ToastContainer position="top-center" autoClose={3000} />

        <Routes>
          <Route
            path="/"
            element={!user ? (
              <LoginForm setUser={setUser} user={user} />
            ) : user.role === 'Viewer' ? (
              <Navigate to="/loading" />
            ) : (
              <Navigate to="/Dashboard" />
            )}
          />
          <Route
            path="/Dashboard"
            element={
              user ? (
                <Content>
                  <Dashboard />
                </Content>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/home"
            element={user ? (
              <Content>
                <HomePage
                  reviewsData={reviewsData}
                  onReportInappropriate={handleReportInappropriate}
                  currentPage={currentPage}
                  totalReviews={totalReviews}
                  onNextPage={handleNextPage}
                  onPreviousPage={handlePreviousPage}
                // isSidebarOpen={isSidebarOpen}
                />
              </Content>
            ) : (
              <Navigate to="/" />
            )}
          />

          <Route
            path="/inbox"
            element={
              user ? (
                <Content>
                  <Inbox />
                </Content>
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/settings"
            element={
              user ? (
                <Content>
                  <Settings user={user} />
                </Content>
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/userinfo"
            element={
              user ? (
                <Content>
                  <UserInfo user={user} />
                </Content>
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route path="/loading" element={<LoadingScreen onBackToLogin={() => setUser(null)} />} />

          <Route
            path="/review_form/:sub"
            element={<ReviewForm />}
          />

        </Routes>
      </Router >
    </ReviewProvider>
  );
};

export default App;